import { useQuery } from '@apollo/client';
import { Autosuggest } from 'braid-design-system';
import React, { useEffect } from 'react';

import {
  GetFilteredDistrictsForProfileDocument,
  ListingDistrictHierarchy,
  GetDistrictsByIdsDocument,
} from 'src/generated/graphql';
import { isNonNullable } from 'src/utils';

interface Props {
  initialValue: string;
  value: AutosuggestValue;
  onChange: (value: AutosuggestValue) => void;
}

export interface AutosuggestValue {
  text: string;
  value?: string;
}

const LocationField = ({ value, initialValue, onChange }: Props) => {
  const { data, error } = useQuery(GetFilteredDistrictsForProfileDocument, {
    variables: {
      searchString: value.text,
      hierarchies: [ListingDistrictHierarchy.Region],
    },
    skip: value.text === '',
  });

  interface DistrictData {
    getDistrictsByIds: {
      districts: Array<{
        id: string;
        displayTitle: string;
      }>;
    };
  }

  interface DistrictDataVars {
    queryInput: string[];
  }

  const districtResult = useQuery<DistrictData, DistrictDataVars>(
    GetDistrictsByIdsDocument,
    {
      variables: {
        queryInput: [initialValue],
      },
    },
  );
  const district = districtResult.data?.getDistrictsByIds?.districts[0];

  useEffect(() => {
    if (district) {
      onChange({
        text: district.displayTitle,
        value: district.id,
      });
    }
  }, [district, onChange]);

  const suggestions =
    data?.filteredListingDistrictsInHierarchies?.districts
      ?.filter(isNonNullable)
      .map((d) => ({
        value: d.id,
        text: d.title,
      })) ?? [];

  return (
    <Autosuggest
      id="location-field"
      label="Location"
      value={value}
      placeholder="Enter city or region"
      tone={error ? 'critical' : 'neutral'}
      message={
        error ? 'Something went wrong trying to find your location.' : ''
      }
      onChange={(v: AutosuggestValue) => {
        onChange(v);
      }}
      onClear={() => {
        onChange({ text: '' });
      }}
      suggestions={suggestions}
      noSuggestionsMessage="No results found."
      automaticSelection
    />
  );
};

export default LocationField;
